import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {getMe} from '../api/auth.js'
import {ApiError} from '../api/client.js'
import {clearToken, getToken} from './token-storage.js'
import {AuthContext} from './auth-context.js'

export function AuthProvider({children}) {
    const [status, setStatus] = useState('loading')
    const [user, setUser] = useState(null)
    const queryClient = useQueryClient()
    //dedupe concurrent validations (provider mount + activation-link refresh) so a
    //single-use token isn't spent by two racing GET /auth/me calls
    const inFlight = useRef(null)

    const validate = useCallback(() => {
        if (inFlight.current)
            return inFlight.current
        inFlight.current = (async () => {
            try {
                const token = getToken()
                if (!token) {
                    setUser(null)
                    setStatus('invalid')
                    return
                }
                try {
                    const {user: me} = await getMe()
                    setUser(me)
                    setStatus('authed')
                } catch (e) {
                    //Only a genuine auth rejection (HTTP 401) means the token is bad — clear it
                    //and require re-pairing. Network failures, timeouts and 5xx are transient:
                    //keep the token and surface a distinct 'error' state so the session is
                    //restored once the server is reachable again, instead of silently logging out.
                    if (e instanceof ApiError && e.status === 401) {
                        clearToken()
                        setUser(null)
                        setStatus('invalid')
                    } else {
                        setStatus('error')
                    }
                }
            } finally {
                inFlight.current = null
            }
        })()
        return inFlight.current
    }, [])

    const logout = useCallback(async () => {
        clearToken()
        setUser(null)
        setStatus('invalid')
        queryClient.clear()
    }, [queryClient])

    useEffect(() => { validate() }, [validate])

    const value = useMemo(() => ({status, user, logout, refresh: validate}), [status, user, logout, validate])

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
